import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Type as TypeIcon, Smile, Trash2, ImagePlus, Loader2, Save, Download,
  RotateCw, Spline, Bold, Sun, PenLine, Library, X, Check,
  Sparkles, Wand2, SquareDashed, Layers as LayersIcon, Droplets,
} from "lucide-react";
import { mistApi, overlaysApi, streamAsset } from "@/api/mist";

/* Curated cross-platform font stack for album-cover typography. */
const FONTS = [
  { label: "Impact", css: "Impact, Haettenschweiler, sans-serif" },
  { label: "Anton", css: '"Anton", Impact, sans-serif' },
  { label: "Serif", css: '"Cormorant Garamond", Georgia, serif' },
  { label: "Georgia", css: "Georgia, serif" },
  { label: "Times", css: '"Times New Roman", Times, serif' },
  { label: "Arial", css: "Arial, Helvetica, sans-serif" },
  { label: "Mono", css: '"JetBrains Mono", "Courier New", monospace' },
  { label: "Script", css: '"Brush Script MT", "Segoe Script", cursive' },
  { label: "Comic", css: '"Comic Sans MS", "Comic Sans", cursive' },
  { label: "Fantasy", css: "fantasy" },
];

const SWATCHES = [
  "#FFFFFF", "#000000", "#FFD028", "#FF4D6D", "#00F5D4", "#9D4EDD",
  "#00B4D8", "#FF6BD6", "#39FF14", "#FF7A00", "#C0C0C0", "#E9B872",
];

const EMOJIS = [
  "😀", "😂", "😍", "🥰", "😎", "🤩", "😭", "😡", "💀", "👑", "🔥", "💯",
  "✨", "⭐", "🌟", "💥", "💫", "⚡", "🌈", "☀️", "🌙", "❤️", "🧡", "💛",
  "💚", "💙", "💜", "🖤", "🤍", "💖", "🎵", "🎶", "🎤", "🎧", "🎸", "🎹",
  "🥁", "🎺", "🎷", "💎", "🚀", "🕊️", "🦋", "🌸", "🌹", "👽", "🤖", "🍾",
];

let _seq = 0;
const uid = () => `l${Date.now()}_${_seq++}`;

const textDefaults = () => ({
  id: uid(), type: "text", text: "YOUR TITLE",
  x: 0.5, y: 0.5, size: 0.13, rot: 0, arc: 0,
  font: FONTS[0].css, bold: true, color: "#FFFFFF",
  stroke: true, strokeColor: "#000000", strokeW: 0.06, shadow: false,
});
const emojiDefaults = (char) => ({
  id: uid(), type: "emoji", char, x: 0.5, y: 0.5, size: 0.18, rot: 0,
});
const imageDefaults = (src, label) => ({
  id: uid(), type: "image", src, img: null, loaded: false, label: label || "sticker",
  x: 0.5, y: 0.5, size: 0.42, rot: 0, opacity: 1, natW: 1, natH: 1,
});
const frameDefaults = (src, label) => ({
  id: uid(), type: "frame", src, img: null, loaded: false, label: label || "frame",
});

/* Ready-made text-sticker style presets (rendered live as CSS/canvas — option C). */
const TEXT_PRESETS = {
  neon: { label: "Neon Glow", patch: { color: "#00F5D4", stroke: false, shadow: false, neon: true, foil: false, bold: true, font: FONTS[1].css, arc: 0 } },
  comic: { label: "Retro Comic", patch: { color: "#FFDE4D", stroke: true, strokeColor: "#000000", strokeW: 0.1, shadow: false, neon: false, foil: false, bold: true, font: FONTS[8].css, arc: 0 } },
  varsity: { label: "Bold Varsity", patch: { color: "#FFFFFF", stroke: true, strokeColor: "#000000", strokeW: 0.09, shadow: true, neon: false, foil: false, bold: true, font: FONTS[1].css, arc: 0 } },
  foil: { label: "Metallic Foil", patch: { foil: true, stroke: true, strokeColor: "#6b5310", strokeW: 0.03, shadow: true, neon: false, bold: true, font: FONTS[0].css, arc: 0 } },
};

/* -------------------------- Canvas render core -------------------------- */
function drawLayer(ctx, l, W, H) {
  const cx = l.x * W, cy = l.y * H;
  const fontPx = l.size * H;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((l.rot * Math.PI) / 180);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;

  if (l.type === "emoji") {
    ctx.font = `${fontPx}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
    if (l.shadow) { ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = fontPx * 0.12; }
    ctx.fillText(l.char, 0, 0);
    ctx.restore();
    return;
  }

  if (l.type === "image") {
    if (!l.img) { ctx.restore(); return; }
    const iw = l.size * W;
    const ih = iw * ((l.natH || 1) / (l.natW || 1));
    ctx.globalAlpha = l.opacity ?? 1;
    ctx.drawImage(l.img, -iw / 2, -ih / 2, iw, ih);
    ctx.restore();
    return;
  }

  ctx.font = `${l.bold ? "bold " : ""}${fontPx}px ${l.font}`;
  if (l.shadow) {
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = fontPx * 0.14;
    ctx.shadowOffsetX = fontPx * 0.04;
    ctx.shadowOffsetY = fontPx * 0.04;
  }
  if (l.neon) {
    ctx.shadowColor = l.color;
    ctx.shadowBlur = fontPx * 0.55;
    ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
  }
  // Metallic foil = vertical gold gradient fill.
  let fillStyle = l.color;
  if (l.foil) {
    const grad = ctx.createLinearGradient(0, -fontPx * 0.6, 0, fontPx * 0.6);
    grad.addColorStop(0, "#fff7cc"); grad.addColorStop(0.42, "#e9b872");
    grad.addColorStop(0.5, "#fffbe6"); grad.addColorStop(0.6, "#c9971f");
    grad.addColorStop(1, "#8a6a12");
    fillStyle = grad;
  }
  const strokeW = l.stroke ? Math.max(1, l.strokeW * fontPx) : 0;
  const chars = [...(l.text || "")];

  if (Math.abs(l.arc) < 1 || chars.length === 0) {
    if (strokeW) { ctx.lineWidth = strokeW; ctx.strokeStyle = l.strokeColor; ctx.strokeText(l.text, 0, 0); }
    ctx.fillStyle = fillStyle; ctx.fillText(l.text, 0, 0);
    if (l.neon) { ctx.shadowBlur = 0; ctx.fillStyle = fillStyle; ctx.fillText(l.text, 0, 0); }
    ctx.restore();
    return;
  }

  // Curved / arc text (rainbow arch when positive, valley when negative).
  const dir = l.arc < 0 ? -1 : 1;
  const span = (Math.abs(l.arc) * Math.PI) / 180;
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((a, b) => a + b, 0) || 1;
  const r = total / span;
  let angle = -span / 2;
  for (let i = 0; i < chars.length; i++) {
    const cw = widths[i];
    const charAngle = (cw / total) * span;
    angle += charAngle / 2;
    ctx.save();
    ctx.rotate(dir * angle);
    ctx.translate(0, -dir * r);
    if (strokeW) { ctx.lineWidth = strokeW; ctx.strokeStyle = l.strokeColor; ctx.strokeText(chars[i], 0, 0); }
    ctx.fillStyle = fillStyle; ctx.fillText(chars[i], 0, 0);
    ctx.restore();
    angle += charAngle / 2;
  }
  ctx.restore();
}

/* Approximate on-canvas hit radius (used for tap-to-select + drag). */
function hitRadius(ctx, l, W, H) {
  const fontPx = l.size * H;
  if (l.type === "emoji") return fontPx * 0.6;
  if (l.type === "image") {
    const iw = l.size * W;
    const ih = iw * ((l.natH || 1) / (l.natW || 1));
    return Math.max(iw, ih) * 0.5;
  }
  ctx.save();
  ctx.font = `${l.bold ? "bold " : ""}${fontPx}px ${l.font}`;
  const w = ctx.measureText(l.text || "A").width;
  ctx.restore();
  if (Math.abs(l.arc) >= 1) return Math.max(w * 0.55, fontPx * 1.4);
  return Math.max(w / 2, fontPx * 0.6) + 6;
}

/* ---------------------------- Vault picker ------------------------------ */
function VaultThumb({ item, onPick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    streamAsset(item.id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [item.id]);
  return (
    <button onClick={() => onPick(item)} data-testid={`overlay-vault-pick-${item.id}`}
      className="relative aspect-square border-2 border-[#00F5D4]/40 overflow-hidden bg-charcoal active:scale-95">
      {src
        ? (item.track === "video"
            ? <video src={src} muted playsInline className="w-full h-full object-cover" />
            : <img src={src} alt="" className="w-full h-full object-cover" />)
        : <Loader2 size={14} className="text-[#00F5D4] animate-spin absolute inset-0 m-auto" />}
    </button>
  );
}

export function VaultPickerModal({ onClose, onPicked, bucket = "images", title = "Pick from Image Vault" }) {
  const [items, setItems] = useState(null);
  useEffect(() => { mistApi.library().then((d) => setItems(d[bucket] || [])).catch(() => setItems([])); }, [bucket]);
  return (
    <div className="absolute inset-0 z-50 bg-black/80 flex items-end" data-testid="overlay-vault-modal">
      <div className="w-full bg-[#0d0d0d] border-t-4 border-[#00F5D4] rounded-t-3xl p-4 max-h-[80dvh] flex flex-col">
        <div className="flex items-center gap-2 pb-3 mb-3 border-b-2 border-[#00F5D4]/20">
          <h3 className="flex-1 font-serif text-xl text-white">{title}</h3>
          <button onClick={onClose} data-testid="overlay-vault-close" className="w-8 h-8 grid place-items-center border-2 border-[#00F5D4] text-[#00F5D4]"><X size={15} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          {items == null ? <div className="grid place-items-center py-10"><Loader2 size={20} className="text-[#00F5D4] animate-spin" /></div>
            : items.length === 0 ? <p className="font-mono text-[10px] text-white/50 py-8 text-center">Nothing in your vault yet — create one first.</p>
              : <div className="grid grid-cols-3 gap-2">{items.map((it) => <VaultThumb key={it.id} item={it} onPick={onPicked} />)}</div>}
        </div>
      </div>
    </div>
  );
}

/* =============================== STUDIO ================================= */
export default function TextOverlayStudio() {
  const canvasRef = useRef(null);
  const scratchRef = useRef(null); // offscreen 2d ctx for measuring
  const [img, setImg] = useState(null); // HTMLImageElement
  const [disp, setDisp] = useState({ w: 320, h: 320 });
  const [layers, setLayers] = useState([]);
  const [selId, setSelId] = useState(null);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null); // {id, filename}
  const fileRef = useRef(null);
  const dragRef = useRef(null);
  const blobUrlsRef = useRef([]);   // track streamAsset blob URLs to revoke on unmount
  useEffect(() => () => { blobUrlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* noop */ } }); }, []);

  // Overlay Builder state
  const [menuTab, setMenuTab] = useState("text"); // text | emoji | frames
  const [frameStyles, setFrameStyles] = useState([]);
  const [genBusy, setGenBusy] = useState("");      // style_key or "ai" while generating
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiKind, setAiKind] = useState("sticker"); // sticker | frame
  const [ovVault, setOvVault] = useState(null);    // accumulated overlay assets
  const [cipherText, setCipherText] = useState("");
  const [cipherKey, setCipherKey] = useState(true);  // include decrypted hint key
  const [cipherBusy, setCipherBusy] = useState(false);

  useEffect(() => { overlaysApi.frameStyles().then((d) => setFrameStyles(d.styles || [])).catch(() => {}); }, []);
  const loadOvVault = useCallback(() => {
    overlaysApi.vault().then((d) => setOvVault(d.items || [])).catch(() => setOvVault([]));
  }, []);
  useEffect(() => { loadOvVault(); }, [loadOvVault]);

  const sel = layers.find((l) => l.id === selId) || null;
  const patchSel = (patch) => setLayers((ls) => ls.map((l) => (l.id === selId ? { ...l, ...patch } : l)));

  // Fit the base image into a mobile-friendly editing box.
  const loadImage = useCallback((image) => {
    const maxW = 340, maxH = 430;
    const scale = Math.min(maxW / image.naturalWidth, maxH / image.naturalHeight, 1.5);
    setDisp({ w: Math.round(image.naturalWidth * scale), h: Math.round(image.naturalHeight * scale) });
    setImg(image);
    setLayers([]); setSelId(null); setSaved(null);
  }, []);

  const onFile = (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    const url = URL.createObjectURL(f);
    const image = new Image();
    image.onload = () => { loadImage(image); URL.revokeObjectURL(url); };
    image.onerror = () => { toast.error("Could not load image"); URL.revokeObjectURL(url); };
    image.src = url;
  };

  const pickVault = (item) => {
    setVaultOpen(false);
    streamAsset(item.id).then((url) => {
      blobUrlsRef.current.push(url);
      const image = new Image();
      image.onload = () => loadImage(image);
      image.onerror = () => toast.error("Could not load image");
      image.src = url;
    }).catch(() => toast.error("Could not load vault image"));
  };

  // Redraw whenever anything changes.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !img) return;
    cv.width = disp.w; cv.height = disp.h;
    const ctx = cv.getContext("2d");
    scratchRef.current = ctx;
    ctx.clearRect(0, 0, disp.w, disp.h);
    ctx.drawImage(img, 0, 0, disp.w, disp.h);
    for (const l of layers) { if (l.type !== "frame") drawLayer(ctx, l, disp.w, disp.h); }
    for (const l of layers) { if (l.type === "frame" && l.img) ctx.drawImage(l.img, 0, 0, disp.w, disp.h); }
    // selection ring (frames are not point-selectable)
    if (sel && sel.type !== "frame") {
      const cx = sel.x * disp.w, cy = sel.y * disp.h;
      const rr = hitRadius(ctx, sel, disp.w, disp.h);
      ctx.save();
      ctx.strokeStyle = "#00F5D4"; ctx.lineWidth = 2; ctx.setLineDash([5, 4]);
      ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
    }
  }, [img, disp, layers, sel]);

  const canvasPoint = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (disp.w / rect.width);
    const py = (e.clientY - rect.top) * (disp.h / rect.height);
    return { px, py };
  };

  const onPointerDown = (e) => {
    if (!img) return;
    const { px, py } = canvasPoint(e);
    const ctx = scratchRef.current;
    // top-most first
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers[i];
      const cx = l.x * disp.w, cy = l.y * disp.h;
      const rr = hitRadius(ctx, l, disp.w, disp.h);
      if (Math.hypot(px - cx, py - cy) <= rr) {
        setSelId(l.id);
        dragRef.current = { id: l.id, ox: px - cx, oy: py - cy };
        canvasRef.current.setPointerCapture(e.pointerId);
        return;
      }
    }
    setSelId(null);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const { px, py } = canvasPoint(e);
    const nx = Math.min(1.1, Math.max(-0.1, (px - d.ox) / disp.w));
    const ny = Math.min(1.1, Math.max(-0.1, (py - d.oy) / disp.h));
    setLayers((ls) => ls.map((l) => (l.id === d.id ? { ...l, x: nx, y: ny } : l)));
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const addText = () => { const l = textDefaults(); setLayers((ls) => [...ls, l]); setSelId(l.id); setSaved(null); };
  const addTextPreset = (key) => {
    const p = TEXT_PRESETS[key];
    const l = { ...textDefaults(), text: p.label.toUpperCase(), ...p.patch };
    setLayers((ls) => [...ls, l]); setSelId(l.id); setSaved(null);
    overlaysApi.track({ asset_type: "text_style", kind: "text", label: p.label, style_key: `text_${key}` }).then(loadOvVault).catch(() => {});
  };
  const addEmoji = (char) => { const l = emojiDefaults(char); setLayers((ls) => [...ls, l]); setSelId(l.id); setSaved(null); };

  // Load an image src into an image/frame layer, caching the HTMLImageElement on the layer.
  const addImageLayer = (src, { frame = false, label } = {}) => {
    if (src.startsWith("blob:")) blobUrlsRef.current.push(src);
    const l = frame ? frameDefaults(src, label) : imageDefaults(src, label);
    setLayers((ls) => [...ls, l]);
    if (!frame) setSelId(l.id);
    setSaved(null);
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => setLayers((ls) => ls.map((x) => (x.id === l.id
      ? { ...x, img: image, loaded: true, natW: image.naturalWidth, natH: image.naturalHeight } : x)));
    image.onerror = () => toast.error("Overlay image failed to load");
    image.src = src;
  };

  // Add a saved vault overlay (sticker/frame) onto the canvas.
  const addVaultOverlay = async (it) => {
    try {
      const url = await streamAsset(it.asset_id);
      addImageLayer(url, { frame: it.kind === "frame", label: it.label });
    } catch { toast.error("Could not load that overlay"); }
  };

  // Ready-made frame: generate (or reuse) a transparent-center frame PNG.
  const pickFrameStyle = async (style) => {
    if (!img) { toast.error("Load a base image first"); return; }
    setGenBusy(style.style_key);
    try {
      const r = await overlaysApi.generate({ kind: "frame", style_key: style.style_key });
      const url = await streamAsset(r.asset_id);
      addImageLayer(url, { frame: true, label: r.label });
      loadOvVault();
      toast.success(`${style.label} frame added & saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Frame generation failed"); }
    finally { setGenBusy(""); }
  };

  // AI Custom Style Builder → generate + auto alpha extract → drop as layer.
  const generateCustom = async () => {
    if (!img) { toast.error("Load a base image first"); return; }
    if (!aiPrompt.trim()) { toast.error("Describe the sticker or frame"); return; }
    setGenBusy("ai");
    try {
      const r = await overlaysApi.generate({ prompt: aiPrompt.trim(), kind: aiKind });
      const url = await streamAsset(r.asset_id);
      addImageLayer(url, { frame: aiKind === "frame", label: r.label });
      loadOvVault();
      setAiPrompt("");
      toast.success(`Custom ${aiKind} created (${r.alpha_engine === "none" ? "no cutout" : "background removed"}) ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(""); }
  };

  const delSel = () => { if (!selId) return; setLayers((ls) => ls.filter((l) => l.id !== selId)); setSelId(null); setSaved(null); };
  const delFrame = (id) => { setLayers((ls) => ls.filter((l) => l.id !== id)); setSaved(null); };

  // Founder's Emoji Cipher → cipher text layer (+ optional hint-key legend) + icon layers.
  const generateCipher = async () => {
    if (!img) { toast.error("Load a base image first"); return; }
    if (!cipherText.trim()) { toast.error("Type a phrase in the Cipher Stream"); return; }
    setCipherBusy(true);
    try {
      const r = await overlaysApi.cipher({ phrase: cipherText.trim(), include_key: cipherKey });
      // Main cipher block (emoji chain) — larger, upper-third.
      const cipher = { ...textDefaults(), text: r.cipher_string, x: 0.5, y: 0.3, size: 0.11, bold: true, stroke: true, strokeColor: "#000000", strokeW: 0.05, color: "#FFFFFF", shadow: true };
      // Optional decrypted hint-key legend beneath.
      const extra = [];
      if (cipherKey && r.key_string) {
        extra.push({ ...textDefaults(), text: r.key_string, x: 0.5, y: 0.44, size: 0.045, bold: true, stroke: true, strokeColor: "#000000", strokeW: 0.06, color: "#FFDE4D", shadow: false });
      }
      setLayers((ls) => [...ls, cipher, ...extra]);
      setSelId(cipher.id); setSaved(null);
      // Drop any auto-generated (alpha-cleared) icon stickers as movable layers.
      let ix = 0;
      for (const t of r.tokens || []) {
        if (t.is_icon && t.asset_id) {
          try {
            const url = await streamAsset(t.asset_id);
            addImageLayer(url, { label: t.word });
            setLayers((ls) => ls.map((l) => (l.src === url ? { ...l, x: 0.25 + ix * 0.2, y: 0.62, size: 0.22 } : l)));
            ix += 1;
          } catch { /* ignore one icon */ }
        }
      }
      loadOvVault();
      toast.success(`Cipher created: ${r.cipher_string}${r.icon_count ? ` (+${r.icon_count} icon)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cipher generation failed"); }
    finally { setCipherBusy(false); }
  };

  // Bake at full image resolution and ingest into the Image Vault.
  const bake = async () => {
    if (!img) { toast.error("Load an image first"); return; }
    setBusy(true);
    try {
      const cap = 2048;
      const nw = img.naturalWidth, nh = img.naturalHeight;
      const scale = Math.min(1, cap / Math.max(nw, nh));
      const W = Math.round(nw * scale), H = Math.round(nh * scale);
      const off = document.createElement("canvas");
      off.width = W; off.height = H;
      const ctx = off.getContext("2d");
      ctx.drawImage(img, 0, 0, W, H);
      for (const l of layers) { if (l.type !== "frame") drawLayer(ctx, l, W, H); }
      for (const l of layers) { if (l.type === "frame" && l.img) ctx.drawImage(l.img, 0, 0, W, H); }
      const blob = await new Promise((res) => off.toBlob(res, "image/png"));
      if (!blob) { toast.error("Bake failed"); return; }
      const fname = `album_cover_${Date.now()}.png`;
      const file = new File([blob], fname, { type: "image/png" });
      const r = await mistApi.ingest(file, "image");
      setSaved({ id: r.id, filename: r.filename || fname });
      window.dispatchEvent(new Event("mist:library-refresh"));
      toast.success("Cover baked & saved to your Image Vault");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Bake failed");
    } finally { setBusy(false); }
  };

  const download = async () => {
    if (!saved) return;
    try {
      const url = await streamAsset(saved.id);
      const a = document.createElement("a");
      a.href = url; a.download = saved.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="h-full w-full flex flex-col bg-black" data-testid="text-overlay-studio">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-3 space-y-3">
        {/* Canvas / uploader */}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="overlay-file-input" />
        {!img ? (
          <div className="space-y-2.5">
            <button onClick={() => fileRef.current?.click()} data-testid="overlay-upload"
              className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#00F5D4]/60 bg-[#0d0d0d] h-48 text-[#00F5D4]/80 active:scale-[0.99]">
              <ImagePlus size={30} strokeWidth={2.2} />
              <span className="font-mono text-[10px] font-black uppercase tracking-widest px-6 text-center">Upload the picture you want to caption</span>
            </button>
            <button onClick={() => setVaultOpen(true)} data-testid="overlay-pick-vault"
              className="w-full flex items-center justify-center gap-2 border-2 border-[#00F5D4] bg-black text-[#00F5D4] py-3 font-mono text-[10px] font-black uppercase tracking-widest active:scale-[0.99]">
              <Library size={15} /> Or pick a generated image from your Vault
            </button>
          </div>
        ) : (
          <>
            <div className="w-full flex justify-center">
              <canvas ref={canvasRef}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
                data-testid="overlay-canvas"
                style={{ width: disp.w, height: disp.h, touchAction: "none" }}
                className="border-[3px] border-[#00F5D4] shadow-[0_0_18px_rgba(0,245,212,0.35)]" />
            </div>

            {/* ===== READY-MADE OVERLAY MENU (sliding categories) ===== */}
            <div className="border-2 border-[#00F5D4]/40 bg-[#0d0d0d]" data-testid="overlay-menu">
              <div className="grid grid-cols-3">
                {[{ k: "text", l: "Text", Icon: TypeIcon }, { k: "emoji", l: "Emoji", Icon: Smile }, { k: "frames", l: "Frames", Icon: SquareDashed }].map((t) => (
                  <button key={t.k} onClick={() => setMenuTab(t.k)} data-testid={`overlay-menu-${t.k}`}
                    className={`flex items-center justify-center gap-1.5 py-2.5 font-mono text-[9px] font-black uppercase tracking-widest border-b-2 ${menuTab === t.k ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-black text-white/55 border-transparent"}`}>
                    <t.Icon size={13} /> {t.l}
                  </button>
                ))}
              </div>
              <div className="p-2">
                {menuTab === "text" && (
                  <div className="grid grid-cols-2 gap-2" data-testid="overlay-text-presets">
                    {Object.entries(TEXT_PRESETS).map(([k, p]) => (
                      <button key={k} onClick={() => addTextPreset(k)} data-testid={`overlay-preset-${k}`}
                        className="border-2 border-white/25 bg-black text-white py-2.5 font-mono text-[10px] font-black uppercase tracking-wide active:scale-95 hover:border-[#00F5D4]">{p.label}</button>
                    ))}
                    <button onClick={addText} data-testid="overlay-add-text"
                      className="col-span-2 flex items-center justify-center gap-1.5 border-2 border-[#00F5D4] bg-black text-[#00F5D4] py-2 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95"><TypeIcon size={13} /> Blank Text Layer</button>
                  </div>
                )}
                {menuTab === "emoji" && (
                  <div className="grid grid-cols-8 gap-1.5" data-testid="overlay-emoji-grid">
                    {EMOJIS.map((em, i) => (
                      <button key={i} onClick={() => addEmoji(em)} data-testid={`overlay-emoji-${i}`}
                        className="aspect-square grid place-items-center text-lg active:scale-90">{em}</button>
                    ))}
                  </div>
                )}
                {menuTab === "frames" && (
                  <div className="grid grid-cols-2 gap-2" data-testid="overlay-frame-styles">
                    {frameStyles.map((s) => (
                      <button key={s.style_key} onClick={() => pickFrameStyle(s)} disabled={!!genBusy} data-testid={`overlay-frame-${s.style_key}`}
                        className="flex items-center justify-center gap-1.5 border-2 border-[#9D4EDD] bg-black text-[#c99bff] py-2.5 font-mono text-[9px] font-black uppercase tracking-wide active:scale-95 disabled:opacity-40">
                        {genBusy === s.style_key ? <Loader2 size={12} className="animate-spin" /> : <SquareDashed size={12} />} {s.label}
                      </button>
                    ))}
                    <p className="col-span-2 font-mono text-[8px] uppercase tracking-widest text-white/40">Frames auto-fit the whole canvas & save to your vault.</p>
                  </div>
                )}
              </div>
            </div>

            {/* ===== AI CUSTOM STYLE BUILDER ===== */}
            <div className="border-2 border-[#FFD028]/60 bg-[#0d0d0d] p-2.5 space-y-2" data-testid="overlay-ai-builder">
              <div className="flex items-center gap-1.5">
                <Wand2 size={13} className="text-[#FFD028]" />
                <span className="font-mono text-[9px] font-black uppercase tracking-widest text-[#FFD028]">AI Custom Style Builder</span>
              </div>
              <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="overlay-ai-prompt"
                placeholder={aiKind === "frame" ? "a glowing neon purple retro frame" : "a shiny 3D gold crown sticker"}
                className="w-full bg-black text-white border-2 border-[#FFD028]/50 px-2.5 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-[#FFD028]" />
              <div className="grid grid-cols-2 gap-2">
                {[{ k: "sticker", l: "Sticker" }, { k: "frame", l: "Frame" }].map((o) => (
                  <button key={o.k} onClick={() => setAiKind(o.k)} data-testid={`overlay-ai-kind-${o.k}`}
                    className={`border-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${aiKind === o.k ? "bg-[#FFD028] text-black border-[#FFD028]" : "bg-black text-white/55 border-white/20"}`}>{o.l}</button>
                ))}
              </div>
              <button onClick={generateCustom} disabled={genBusy === "ai"} data-testid="overlay-ai-generate"
                className="w-full flex items-center justify-center gap-2 bg-[#FFD028] text-black font-black font-mono text-[10px] uppercase tracking-widest py-2.5 border-2 border-[#FFD028] active:translate-y-[1px] disabled:opacity-50">
                {genBusy === "ai" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {genBusy === "ai" ? "Creating & cutting out…" : "Generate + Auto Cut-Out"}
              </button>
            </div>

            {/* ===== FOUNDER'S EMOJI CIPHER (Phase 2) ===== */}
            <div className="border-2 border-[#9D4EDD]/70 bg-[#0d0d0d] p-2.5 space-y-2" data-testid="overlay-cipher">
              <div className="flex items-center gap-1.5">
                <Sparkles size={13} className="text-[#c99bff]" />
                <span className="font-mono text-[9px] font-black uppercase tracking-widest text-[#c99bff]">Founder's Cipher Stream</span>
              </div>
              <input value={cipherText} onChange={(e) => setCipherText(e.target.value)} data-testid="overlay-cipher-input"
                placeholder="The secret drop happens at midnight"
                className="w-full bg-black text-white border-2 border-[#9D4EDD]/50 px-2.5 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-[#9D4EDD]" />
              <button onClick={() => setCipherKey((v) => !v)} data-testid="overlay-cipher-key-toggle"
                className={`w-full flex items-center justify-between border-2 px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${cipherKey ? "bg-[#9D4EDD] text-white border-[#9D4EDD]" : "bg-black text-white/55 border-white/20"}`}>
                <span>Include Decrypted Hint Key</span>
                <span className={`px-1.5 py-0.5 border-2 ${cipherKey ? "border-white text-white" : "border-white/40 text-white/40"}`}>{cipherKey ? "ON" : "OFF"}</span>
              </button>
              <button onClick={generateCipher} disabled={cipherBusy} data-testid="overlay-cipher-generate"
                className="w-full flex items-center justify-center gap-2 bg-[#9D4EDD] text-white font-black font-mono text-[10px] uppercase tracking-widest py-2.5 border-2 border-[#9D4EDD] active:translate-y-[1px] disabled:opacity-50">
                {cipherBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {cipherBusy ? "Encoding cipher…" : "Encode → Drop Cipher Layer"}
              </button>
            </div>

            {/* ===== MY VAULT / PREMIUM OVERLAYS ===== */}
            {ovVault && ovVault.length > 0 && (
              <div className="border-2 border-[#00F5D4]/40 bg-[#0d0d0d] p-2.5" data-testid="overlay-my-vault">
                <div className="flex items-center gap-1.5 mb-2">
                  <LayersIcon size={13} className="text-[#00F5D4]" />
                  <span className="font-mono text-[9px] font-black uppercase tracking-widest text-[#00F5D4]">My Vault · Premium Overlays</span>
                  <span className="ml-auto font-mono text-[8px] font-black bg-[#FFD028] text-black px-1.5 py-0.5">{ovVault.length}</span>
                </div>
                <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="overlay-vault-strip">
                  {ovVault.filter((it) => it.asset_id).map((it) => (
                    <OverlayVaultThumb key={it.custom_asset_id} item={it} onPick={() => addVaultOverlay(it)} />
                  ))}
                </div>
              </div>
            )}

            {/* Swap base image */}
            <button onClick={() => fileRef.current?.click()} data-testid="overlay-change-image"
              className="w-full flex items-center justify-center gap-1.5 border-2 border-white/30 bg-black text-white/60 py-2 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95"><ImagePlus size={13} /> Swap base image</button>

            {/* Layer chips */}
            {layers.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="overlay-layers">
                {layers.map((l) => {
                  const on = l.id === selId;
                  return (
                    <button key={l.id} onClick={() => setSelId(l.id)} data-testid={`overlay-layer-${l.id}`}
                      className={`shrink-0 max-w-[90px] truncate border-2 px-2 py-1.5 font-mono text-[9px] font-black uppercase tracking-wide ${on ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-[#0d0d0d] text-white/60 border-white/20"}`}>
                      {l.type === "emoji" ? l.char
                        : l.type === "image" ? `🃏 ${l.label || "sticker"}`
                        : l.type === "frame" ? `▢ ${l.label || "frame"}`
                        : (l.text || "text")}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Selected-layer controls */}
            {sel && (
              <div className="border-2 border-[#00F5D4]/40 bg-[#0d0d0d] p-3 space-y-3" data-testid="overlay-controls">
                {sel.type === "text" && (
                  <>
                    <label className="block">
                      <span className="font-mono text-[8px] uppercase tracking-widest text-white/50">Text</span>
                      <input value={sel.text} onChange={(e) => patchSel({ text: e.target.value })} data-testid="overlay-text-input"
                        className="mt-1 w-full bg-black text-white border-2 border-[#00F5D4]/50 px-2.5 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-[#00F5D4]" />
                    </label>

                    <div>
                      <span className="font-mono text-[8px] uppercase tracking-widest text-white/50">Font</span>
                      <div className="mt-1 flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="overlay-fonts">
                        {FONTS.map((f) => (
                          <button key={f.label} onClick={() => patchSel({ font: f.css })} data-testid={`overlay-font-${f.label}`}
                            style={{ fontFamily: f.css }}
                            className={`shrink-0 border-2 px-2.5 py-1.5 text-sm ${sel.font === f.css ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-black text-white border-white/20"}`}>{f.label}</button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button onClick={() => patchSel({ bold: !sel.bold })} data-testid="overlay-bold"
                        className={`flex items-center gap-1 border-2 px-3 py-1.5 font-mono text-[9px] font-black uppercase ${sel.bold ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-black text-white/60 border-white/20"}`}><Bold size={12} /> Bold</button>
                      <button onClick={() => patchSel({ stroke: !sel.stroke })} data-testid="overlay-outline"
                        className={`flex items-center gap-1 border-2 px-3 py-1.5 font-mono text-[9px] font-black uppercase ${sel.stroke ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-black text-white/60 border-white/20"}`}><PenLine size={12} /> Outline</button>
                      <button onClick={() => patchSel({ shadow: !sel.shadow })} data-testid="overlay-shadow"
                        className={`flex items-center gap-1 border-2 px-3 py-1.5 font-mono text-[9px] font-black uppercase ${sel.shadow ? "bg-[#00F5D4] text-black border-[#00F5D4]" : "bg-black text-white/60 border-white/20"}`}><Sun size={12} /> Shadow</button>
                    </div>

                    <ColorRow label="Text colour" value={sel.color} onChange={(c) => patchSel({ color: c })} testid="overlay-color" />
                    {sel.stroke && <ColorRow label="Outline colour" value={sel.strokeColor} onChange={(c) => patchSel({ strokeColor: c })} testid="overlay-stroke-color" />}

                    <Slider label="Curve / Arc" value={sel.arc} min={-180} max={180} step={2} onChange={(v) => patchSel({ arc: v })} testid="overlay-arc" Icon={Spline} suffix="°" />
                  </>
                )}

                {sel.type !== "frame" && (
                  <>
                    <Slider label="Size" value={Math.round(sel.size * 100)} min={4} max={sel.type === "image" ? 100 : 45} step={1} onChange={(v) => patchSel({ size: v / 100 })} testid="overlay-size" />
                    <Slider label="Rotate" value={sel.rot} min={-180} max={180} step={2} onChange={(v) => patchSel({ rot: v })} testid="overlay-rotate" Icon={RotateCw} suffix="°" />
                  </>
                )}

                {sel.type === "image" && (
                  <Slider label="Opacity" value={Math.round((sel.opacity ?? 1) * 100)} min={10} max={100} step={5} onChange={(v) => patchSel({ opacity: v / 100 })} testid="overlay-opacity" Icon={Droplets} suffix="%" />
                )}
                {sel.type === "frame" && (
                  <p className="font-mono text-[8px] uppercase tracking-widest text-white/40" data-testid="overlay-frame-hint">Full-canvas frame overlay — always sits on top.</p>
                )}
                {sel.type === "emoji" && (
                  <p className="font-mono text-[8px] uppercase tracking-widest text-white/40" data-testid="overlay-emoji-hint">Tap a text chip above to edit fonts, colours & arc.</p>
                )}

                <button onClick={delSel} data-testid="overlay-delete"
                  className="w-full flex items-center justify-center gap-1.5 border-2 border-[#FF4D6D] bg-black text-[#FF4D6D] py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95"><Trash2 size={13} /> Delete layer</button>
              </div>
            )}

            {saved && (
              <div className="border-[3px] border-[#00F5D4] bg-[#0d0d0d] p-2.5 shadow-[0_0_18px_rgba(0,245,212,0.35)]" data-testid="overlay-saved">
                <span className="font-mono text-[8px] uppercase tracking-widest bg-[#00F5D4] text-black px-2 py-0.5">Saved to vault</span>
                <button onClick={download} data-testid="overlay-download"
                  className="mt-2 w-full flex items-center justify-center gap-2 border-2 border-[#FFD028] bg-black text-[#FFD028] py-3 font-mono text-[10px] font-black uppercase tracking-widest active:scale-95"><Download size={14} /> Download cover</button>
              </div>
            )}
          </>
        )}
      </div>

      {img && (
        <div className="shrink-0 border-t-2 border-[#00F5D4]/40 bg-black px-4 py-3">
          <button onClick={bake} disabled={busy} data-testid="overlay-bake"
            className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-[#00F5D4] shadow-[0_0_18px_rgba(0,245,212,0.5)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {busy ? "Baking cover…" : "Bake & Save Cover"}
          </button>
        </div>
      )}

      {vaultOpen && <VaultPickerModal onClose={() => setVaultOpen(false)} onPicked={pickVault} />}
    </div>
  );
}

function OverlayVaultThumb({ item, onPick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    streamAsset(item.asset_id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [item.asset_id]);
  return (
    <button onClick={onPick} data-testid={`overlay-vault-item-${item.custom_asset_id}`}
      className="shrink-0 w-16 relative border-2 border-[#00F5D4]/40 bg-[#141414] active:scale-95"
      style={{ imageRendering: "auto" }}>
      <div className="aspect-square grid place-items-center overflow-hidden"
        style={{ backgroundImage: "linear-gradient(45deg,#222 25%,transparent 25%),linear-gradient(-45deg,#222 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#222 75%),linear-gradient(-45deg,transparent 75%,#222 75%)", backgroundSize: "10px 10px", backgroundPosition: "0 0,0 5px,5px -5px,-5px 0" }}>
        {src ? <img src={src} alt={item.label} className="w-full h-full object-contain" />
          : <Loader2 size={12} className="text-[#00F5D4] animate-spin" />}
      </div>
      <span className="block truncate font-mono text-[6px] uppercase tracking-wide text-white/70 px-0.5 py-0.5">{item.label}</span>
      {item.kind === "frame" && <span className="absolute top-0 left-0 bg-[#9D4EDD] text-white font-mono text-[6px] px-1">FRAME</span>}
    </button>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <span className="font-mono text-[8px] uppercase tracking-widest text-white/50">{label}</span>
      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
        {SWATCHES.map((c) => (
          <button key={c} onClick={() => onChange(c)} data-testid={`${testid}-${c}`}
            className={`w-6 h-6 border-2 ${value.toUpperCase() === c ? "border-[#00F5D4] ring-2 ring-[#00F5D4]" : "border-white/20"}`}
            style={{ backgroundColor: c }}>{value.toUpperCase() === c && <Check size={12} className="mx-auto text-black mix-blend-difference" />}</button>
        ))}
        <label className="w-6 h-6 border-2 border-white/40 overflow-hidden grid place-items-center cursor-pointer" style={{ backgroundColor: value }}>
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-custom`} className="opacity-0 w-8 h-8 cursor-pointer" />
        </label>
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, step, onChange, testid, Icon, suffix = "" }) {
  return (
    <div>
      <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-widest text-white/50">
        <span className="flex items-center gap-1">{Icon && <Icon size={11} />} {label}</span>
        <span className="text-[#00F5D4]">{value}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(+e.target.value)} data-testid={testid} className="w-full accent-[#00F5D4]" />
    </div>
  );
}
